<template>
  <template v-if="$slots.buttonActive && $slots.buttonInactive">
    <template v-if="isExpanded">
      <button
        ref="buttonOutsideClick"
        type="button"
        class="active cursor-pointer w-full"
        :aria-expanded="isExpanded"
        :aria-controls="panelId"
        :title="buttonTitle"
        @click="isExpanded = !isExpanded"
      >
        <slot name="buttonActive" />
      </button>
    </template>
    <template v-if="!isExpanded">
      <button
        ref="buttonOutsideClick"
        type="button"
        class="inactive cursor-pointer w-full"
        :aria-expanded="isExpanded"
        :aria-controls="panelId"
        :title="buttonTitle"
        @click="isExpanded = !isExpanded"
      >
        <slot name="buttonInactive" />
      </button>
    </template>
  </template>
  <template v-if="$slots.head">
    <button
      type="button"
      :class="[{'active' : isExpanded},{'inactive' : !isExpanded}, 'cursor-pointer w-full']"
      :aria-expanded="isExpanded"
      :aria-controls="panelId"
      :title="buttonTitle"
      @click="isExpanded = !isExpanded"
    >
      <slot name="head" />
    </button>
  </template>
  <template v-if="$slots.collapse">
    <div :id="panelId" role="region">
      <Collapse :when="isExpanded" class="v-collapse">
        <slot name="collapse" />
      </Collapse>
    </div>
  </template>
  <template v-if="$slots.collapseAbsolute">
    <div :id="panelId" ref="onOutsideClick" :class="['absolute', props.absolutePosition]" role="region">
      <Collapse :when="isExpanded" class="v-collapse">
        <slot name="collapseAbsolute" />
      </Collapse>
    </div>
  </template>
  <template v-if="$slots.bottom">
    <button
      type="button"
      :class="[{'active' : isExpanded}, {'inactive' : !isExpanded}, 'cursor-pointer w-full']"
      :aria-expanded="isExpanded"
      :aria-controls="panelId"
      @click="isExpanded = !isExpanded"
    >
      <slot name="bottom" />
    </button>
  </template>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { Collapse } from 'vue-collapsed'

const props = defineProps({
  outsideClick: {
    type: Boolean,
    default: false
  },
  absolutePosition: {
    type: String,
    default: ''
  },
  buttonTitle: {
    type: String,
    default: ''
  }
})

const isExpanded = ref(false)
const panelId = `collapsed-panel-${useId().replace(/\W/g, '')}`

const onOutsideClick = ref()
const buttonOutsideClick = ref()
const listener = (event) => {
  if (props.outsideClick && !onOutsideClick.value?.contains(event.target) && !buttonOutsideClick.value?.contains(event.target)) {
    isExpanded.value = false
  }
}
const open = () => {
  isExpanded.value = true
}

const onKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && isExpanded.value) {
    isExpanded.value = false
  }
}

defineExpose({ isExpanded, open })
onMounted(() => {
  window.addEventListener('mouseup', listener)
  window.addEventListener('keydown', onKeydown)
})
onBeforeUnmount(() => {
  window.removeEventListener('mouseup', listener)
  window.removeEventListener('keydown', onKeydown)
})
</script>

<style scoped>
@reference "~/assets/css/main.css";
.v-collapse {
  @apply transition-[height] duration-700 ease-in-out;
}
.v-collapse[data-collapse='expanded']{
  @apply opacity-100;
}

.v-collapse[data-collapse='collapsed']{
  @apply h-0 opacity-0;
}
</style>
